<script setup lang="ts">
import * as yup from "yup"
import { useOperationStore } from "~/stores/operation.store"
import { cloneDeep } from "lodash-es"
import type { components } from "~/assets/api/openapi"

type ApplicationType = "RULE_APPROBATION" | "EU" | "THIRD_PARTY"

const operationStore = useOperationStore()

const schema = yup.object().shape({
  studyBeforeEuAccession: yup.boolean().when([], {
    is: () => applicationType.value === "EU",
    then: (schema) => schema.required("Pflichtfeld"),
    otherwise: (schema) => schema.strip(),
  }),

  result: yup
    .string()
    .required("Pflichtfeld")
    .test(
      "is-not-available",
      "Der Arbeitsschritt kann nicht abgeschlossen werden, solange das Ergebnis nicht vorliegt",
      yupTestOnlyOnCreate((value) => value !== "notAvailable")
    ),

  completionDate: yup.string().when("result", {
    is: (v: string) => v && v !== "notAvailable",
    then: (s) => s.required("Pflichtfeld"),
    otherwise: (s) => s.strip(),
  }),

  startOfTerm: yup.string(),
})

type Schema = yup.InferType<typeof schema>

const currentWorkStepData = computed<Schema>(
  () =>
    (operationStore.currentWorkStep?.data ?? {
      result: undefined,
    }) as unknown as Schema
)

const state = reactive<Schema>(cloneDeep(currentWorkStepData.value))

const applicationType = computed<ApplicationType | undefined>(() => {
  const textKey = operationStore.findFormDataValueByTextKey<
    components["schemas"]["LocaleValue"]
  >("gsb.ah.applicationDataFields.applicationType")?.textKey

  const lastPart = textKey?.split(".").pop()
  return lastPart as ApplicationType | undefined
})

const isCompletionDateFieldEnabled = computed(() => {
  return state.result === "notAvailable"
})

const resultOptions = computed<IffRadioGroupOption[]>(() =>
  [
    {
      label: "Liegt noch nicht vor",
      value: "notAvailable",
    },
    {
      label: "Abgeschlossenheit festgestellt auf Basis internen Aktenstudiums",
      value: "internalFileReview",
    },
    {
      label: "Abgeschlossenheit festgestellt / Positiver Feststellungsbescheid",
      value: "positiveDecision",
    },
    {
      label:
        "Abgeschlossenheit festgestellt auf Basis eines vorläufigen Diploms",
      value: "preliminaryDiploma",
      condition: () => applicationType.value === "EU",
    },
    {
      label: "Abgeschlossenheit kann nicht festgestellt werden",
      value: "notDetermined",
    },
    {
      label:
        "Abgeschlossenheit kann nicht festgestellt werden, Berufserlaubnis möglich",
      value: "notDeterminedWithBE",
    },
  ].filter((option) => !option.condition || option.condition())
)

const showStartOfTerm = computed<boolean>(() => {
  return (
    state.studyBeforeEuAccession === false &&
    (state.result === "internalFileReview" ||
      state.result === "positiveDecision" ||
      state.result === "preliminaryDiploma")
  )
})

watch(isCompletionDateFieldEnabled, (isEnabled) => {
  if (!isEnabled) {
    state.completionDate = undefined
  }
})
</script>

<template>
  <IffOperationWorkStepFormV2 :state="state" :schema="schema">
    <template #above-form>
      <IffReviewsSection
        v-if="operationStore.currentWorkStep"
        :work-step-id="operationStore.currentWorkStep?.id"
        additional-headline="Prüfung Abgeschlossenheit"
      />
    </template>

    <IffContentSection
      v-if="applicationType === 'EU'"
      headline="Studium prüfen"
    >
      <IffDualRadioGroup
        legend="Wurde das Studium vor oder nach EU-Betritt des Ausbildungslandes abgeschlossen?"
        name="studyBeforeEuAccession"
        v-model="state.studyBeforeEuAccession"
        required
        :options="[
          { label: 'vor EU-Beitritt', value: true },
          { label: 'nach EU-Beitritt', value: false },
        ]"
      />
    </IffContentSection>

    <IffContentSection
      headline="Abschließendes Ergebnis der Abgeschlossenheitsprüfung"
      :is-last-child="true"
    >
      <IffRadioGroup
        legend="Ergebnis"
        name="result"
        v-model="state.result"
        :options="resultOptions"
        required
        aria-controls="completion-date-region"
      />

      <div id="completion-date-region" aria-live="polite">
        <IffTransitionHeight>
          <UFormGroup
            v-if="!isCompletionDateFieldEnabled"
            name="completionDate"
            label="Datum der Feststellung"
            required
            class="max-w-[200px]"
          >
            <template #hint>
              <UButton
                @click="
                  state.completionDate = new Date().toISOString().slice(0, 10)
                "
                icon="i-ic-outline-access-time"
                size="sm"
                color="gray"
                variant="ghost"
                :trailing="false"
                aria-label="Aktuelles Datum wählen"
                title="Aktuelles Datum wählen"
              />
            </template>
            <UInput
              v-model="state.completionDate"
              type="date"
              aria-required="true"
              class="whitespace-normal max-w-[150px]"
            />
          </UFormGroup>
        </IffTransitionHeight>
      </div>

      <div aria-live="polite">
        <IffTransitionHeight>
          <UFormGroup
            v-if="showStartOfTerm"
            name="startOfTerm"
            label="Fristbeginn"
            required
            class="max-w-[170px]"
          >
            <template #hint>
              <IffIconTooltip
                text="BQFG: Wert wird in Datum der Vollständigkeit der vorzulegenden Unterlagen (e07) übernommen."
              />
              <UButton
                @click="
                  state.startOfTerm = new Date().toISOString().slice(0, 10)
                "
                icon="i-ic-outline-access-time"
                size="sm"
                color="gray"
                variant="ghost"
                :trailing="false"
                aria-label="Aktuelles Datum wählen"
                title="Aktuelles Datum wählen"
              />
            </template>
            <UInput
              v-model="state.startOfTerm"
              type="date"
              aria-required="true"
              class="whitespace-normal max-w-[150px]"
            />
          </UFormGroup>
        </IffTransitionHeight>
      </div>
    </IffContentSection>
  </IffOperationWorkStepFormV2>
</template>
